import { Component, OnInit, OnDestroy } from "@angular/core";
import { UserManagementService } from "src/app/user-management/user-management.service";
import { SessionService } from "src/app/session.service";
import { environment } from "src/environments/environment";
import { Router, NavigationEnd } from "@angular/router";
import { filter } from "rxjs/operators";
import { ExecutiveHighlightService } from "../shared/ExecutiveHighlightService.service";

@Component({
    selector: "app-user-management-sites",
    templateUrl: "./user-management-sites.component.html",
    styleUrls: ["./user-management-sites.component.css"],
    standalone: false
})

export class UserManagementSitesComponent implements OnInit {
  private storage = environment.storage;
  users: any[] = [];
  plant: any[] = [];
  teamMembers = [];
  executiveMembers = [];
  /** Sites ticked for a bulk unassign, by site id. */
  selectedSiteIds = new Set<string>();
  /** Sites awaiting confirmation; null when no prompt is open. */
  pendingUnassign: { siteIds: string[]; names: string[] } | null = null;
  unassigning = false;
  /**
   * Executives that actually have an account to switch into, keyed by site.
   * Keyed rather than index-aligned with teamMembers: the plants and executives
   * lists are fetched separately and need not line up row for row.
   */
  executiveBySite: { [siteId: string]: any } = {};
  loginForm: any;
  errorMessage: string = "";
  executive: any[] = [];
  showLeavePopup = false;
  private pendingPopStateEvent: PopStateEvent | null = null;
  popStateListener: any;
  popupOpen = false;
  showCheckboxes = false;
  showExecutiveConfig = false;
  executiveConfigChoice: 'yes' | 'no' = 'no';
  siteSearch: string = '';
  availableSites: any[] = [];
  selectedSitesForConfig: any[] = [];
  executiveConfigSaved = false;
  summaryData: any[] = [];
  showSummaryPopup = false;
  apiProcessingDone = false;
  apiProcessingInterval: any;
  executiveApiProcessingDone = false;
  executiveCountdown: number = 10;
  executiveCountdownInterval: any;

  constructor(
    private userService: UserManagementService,
    private router: Router,
    private sessionService: SessionService,
    private executiveHighlightService: ExecutiveHighlightService
  ) {}

  ngOnInit(): void {
     this.fetchAvailableSites();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (event.url === "/user-management-sites") {
          this.fetchUsers();
        }
      });
    this.fetchUsers();
    this.fetchUsersE();
    sessionStorage.setItem("visitedUserManagementSites", "true");
    
    // Store the original admin role (16) when first visiting this page
    const currentUser = this.userService.getUserDetail();
    if (Number(currentUser?.role ?? currentUser?.user_role) === 16) {
      sessionStorage.setItem("originalAdminRole", "16");
    }

    if (
      window.history.state === null ||
      window.history.state !== "user-management-sites"
    ) {
      history.replaceState("user-management-sites", "", this.router.url);
      history.pushState(null, "", this.router.url);
    }

    this.popStateListener = (event: PopStateEvent) => {
      const targetPath = window.location.pathname;
      if (
        targetPath === "/user-management-sites" &&
        sessionStorage.getItem("visitedUserManagementSites") === "true"
      ) {
        this.pendingPopStateEvent = event;
        this.showLeavePopup = true;
        history.pushState(null, "", this.router.url);
      }
    };
    window.addEventListener("popstate", this.popStateListener);
  }
  ngOnDestroy(): void {
    window.removeEventListener("popstate", this.popStateListener);
  }
  onStay() {
    this.showLeavePopup = false;
    this.pendingPopStateEvent = null;
  }

  onLeave() {
    this.showLeavePopup = false;
    this.pendingPopStateEvent = null;
    this.logout();
  }

  logout() {
    this.userService.logout();
    sessionStorage.removeItem("visitedUserManagementSites");
    this.router.navigate(["/login"]);
  }

  fetchUsers() {
    const admin_user_id = Number(this.userService.getAuthenticatedUserId());
    sessionStorage.setItem('admin_id', admin_user_id.toString());
    this.userService.getPlants(admin_user_id).subscribe((response) => {
      if (response.status === 1) {
        this.plant = response.plants;
        this.teamMembers = this.plant.map((plant, index) => {
          const mappedMember = {
            name: `${plant.site_name}`,
            site_id: plant.site_id,
            user_id: plant.user_id,
            admin_id: plant.admin_user_id,
          };
          return mappedMember;
        });
      }
    });
  }

  fetchUsersE() {
    const admin_user_id = Number(this.userService.getAuthenticatedUserId());
    this.userService.getExecutive(admin_user_id).subscribe((response) => {
      if (response.status === 1) {
        this.executive = response.executives;
        this.executiveMembers = this.executive
          // executive_id is optional on a site mapping. Without one there is no
          // account to switch into, so the site is not offered as an executive.
          .filter((exec) => exec.executive_id !== null && exec.executive_id !== undefined)
          .map((exec) => ({
            name: `${exec.site_name}`,
            site_id: exec.site_id,
            user_id: exec.executive_id,
            admin_id: exec.admin_user_id,
          }));

        this.executiveBySite = {};
        this.executiveMembers.forEach((exec) => {
          this.executiveBySite[exec.site_id] = exec;
        });
      }
    });
  }

  isSelected(member: any): boolean {
    return this.selectedSiteIds.has(String(member?.site_id));
  }

  toggleSelected(member: any): void {
    const siteId = String(member?.site_id);
    if (this.selectedSiteIds.has(siteId)) {
      this.selectedSiteIds.delete(siteId);
    } else {
      this.selectedSiteIds.add(siteId);
    }
  }

  get selectedCount(): number {
    return this.selectedSiteIds.size;
  }

  /** Prompt for one site, or for everything ticked. */
  askUnassign(member?: any): void {
    const chosen = member
      ? [member]
      : this.teamMembers.filter((entry) => this.isSelected(entry));

    if (!chosen.length) {
      return;
    }

    this.pendingUnassign = {
      siteIds: chosen.map((entry) => String(entry.site_id)),
      names: chosen.map((entry) => entry.name),
    };
  }

  cancelUnassign(): void {
    this.pendingUnassign = null;
  }

  get unassignPrompt(): string {
    if (!this.pendingUnassign) {
      return '';
    }

    const { names } = this.pendingUnassign;
    return names.length === 1
      ? `Are you sure you want to unassign ${names[0]} from your account?`
      : `Are you sure you want to unassign these ${names.length} sites from your account?`;
  }

  confirmUnassign(): void {
    if (!this.pendingUnassign || this.unassigning) {
      return;
    }

    const siteIds = this.pendingUnassign.siteIds;
    this.unassigning = true;

    this.userService.unassignSites(siteIds).subscribe({
      next: (response: any) => {
        this.unassigning = false;
        if (response?.status !== 1) {
          this.errorMessage = response?.message || 'Unable to unassign the selected sites.';
          return;
        }

        // Drop them locally as well as refetching: the list should settle even
        // if the reload is slow, and a stale executive entry would otherwise
        // keep offering a site that is gone.
        const removed = new Set(siteIds);
        this.teamMembers = this.teamMembers.filter((entry) => !removed.has(String(entry.site_id)));
        removed.forEach((siteId) => {
          delete this.executiveBySite[siteId];
          this.selectedSiteIds.delete(siteId);
        });

        this.pendingUnassign = null;
        this.fetchUsers();
        this.fetchUsersE();
      },
      error: () => {
        this.unassigning = false;
        this.errorMessage = 'Unable to unassign the selected sites.';
      },
    });
  }

  private handleSwitchResponse(response: any): void {
    if (!this.userService.applyAuthenticatedSession(response)) {
      this.errorMessage = response?.message || response?.msg || "Unable to switch user.";
      return;
    }

    this.sessionService.resetSessionTimeout();

    if (response.user.role === 16) {
      this.router.navigate(["/user-management-sites"]).then(() => {});
    } else if (response.user.role === 13) {
      this.router.navigate(["reportlibrary/editreport/process"]);
    } else {
      this.router.navigate(["/"]).then(() => {
        window.location.reload();
      });
    }
  }

  switchUser(mappedMember: { site_id: number; user_id: number; name?: string }) {
    localStorage.setItem("dashboardSwitcherMode", "plant");
    localStorage.setItem("currentSiteId", mappedMember.site_id.toString());
    localStorage.setItem("currentSiteName", mappedMember.name || "");
    localStorage.setItem("currentDashboardType", "plant");
    const adminId = Number(sessionStorage.getItem("admin_id"));

    this.userService.switchUser(mappedMember.user_id, adminId).subscribe(
      (response: any) => {
        this.handleSwitchResponse(response);
      },
      (error) => {}
    );
  }

  switchExecutive(
    mappedExecutive: { site_id: number; user_id: number; name?: string },
    executive: any,
    index: number
  ) {
    this.executiveHighlightService.setHighlight(index, 12);
    localStorage.setItem("dashboardSwitcherMode", "executive");
    localStorage.setItem("dashboardSwitcherExecutiveIndex", index.toString());
    localStorage.setItem("currentSiteId", mappedExecutive.site_id.toString());
    localStorage.setItem("currentSiteName", mappedExecutive.name || "");
    localStorage.setItem("currentDashboardType", "executive");
    const adminId = Number(sessionStorage.getItem("admin_id"));

    this.userService.switchUser(mappedExecutive.user_id, adminId).subscribe(
      (response: any) => {
        this.handleSwitchResponse(response);
      },
      (error) => {}
    );
  }

  openPopup() {
    this.popupOpen = true;
    this.showCheckboxes = false;
  }

  closePopup() {
    if (!this.executiveConfigSaved) {
      this.showCheckboxes = false;
      this.showExecutiveConfig = false;
      this.executiveConfigChoice = 'no';
      this.siteSearch = '';
      this.selectedSitesForConfig = [];
      this.availableSites.forEach(site => {
        site.execSelected = false;
        site.executiveUsername = '';
        site.executivePassword = '';
      });
      this.popupOpen = false;
    } else {
      this.popupOpen = false;
    }
  }

  showSummary() {
    this.popupOpen = true;
    this.showCheckboxes = false;
    this.showExecutiveConfig = false;
  }

  onSiteCheckboxChange(site: any) {
    if (this.executiveConfigSaved) {
      this.executiveConfigSaved = false;
      this.showExecutiveConfig = false;
    }
    this.saveSelectedSiteIds();
  }

  submitSelectedSites() {
    this.selectedSitesForConfig = this.availableSites.filter(site => site.selected);
    this.showExecutiveConfig = true;
  }
  
  fetchAvailableSites() {
    this.userService.getAvailableSites().subscribe(
      (sites) => {
      const selectedIds: number[] = JSON.parse(localStorage.getItem('selectedSiteIds') || '[]');
      this.availableSites = sites.map(site => ({
        ...site,
        selected: selectedIds.includes(site.site_id)
      }));
    },
      (error) => {}
    );
  }

  get filteredSites() {
    if (!this.siteSearch) return this.availableSites;
    return this.availableSites.filter(site =>
      (site.site_name || '').toLowerCase().includes(this.siteSearch.toLowerCase())
    );
  }

  onAddSitesClick() {
    this.fetchAvailableSites();
    this.showCheckboxes = true;
  }

    startApiProcessingStatusPolling() {
      this.apiProcessingDone = false;

      this.userService.getSiteSetupStatus(this.summaryData).subscribe(
        (res: any) => {
          if (res.sites && Array.isArray(res.sites)) {
            this.summaryData = this.summaryData.map(site => {
              const backendSite = res.sites.find((s: any) => s.site_id === site.site_id);
              const merged = backendSite ? { ...site, ...backendSite } : site;
              return merged;
            });

            const executiveCredentials = this.summaryData
              .filter(site => site.executiveUsername && site.executivePassword)
              .map(site => ({
                site_id: site.site_id,
                executiveUsername: site.executiveUsername,
                executivePassword: site.executivePassword
              }));

            if (executiveCredentials.length > 0) {
              this.executiveApiProcessingDone = false;
              this.userService.setupExecutiveForPlant(executiveCredentials).subscribe(
                (response: any) => {
                  this.executiveApiProcessingDone = true;
                  this.executiveCountdown = 15;
                  this.executiveCountdownInterval = setInterval(() => {
                  this.executiveCountdown--;
                  if (this.executiveCountdown <= 0) {
                    clearInterval(this.executiveCountdownInterval);
                        this.showSummaryPopup = false;
                  }
                  }, 1000);
                  let finalExecutiveSiteArray: any[] = [];

                  if (response.results && Array.isArray(response.results)) {
                    const admin_user_id = Number(this.userService.getAuthenticatedUserId());

                    finalExecutiveSiteArray = this.summaryData.map(site => {
                      const execSite = response.results.find((s: any) => s.site_id === site.site_id);
                      let merged = execSite ? { ...site, ...execSite } : site;
                      const { executiveUsername, executivePassword, ...rest } = merged;
                      return { ...rest, admin_user_id };
                    });

                    this.userService.finalPlantCustomization(finalExecutiveSiteArray).subscribe(
                      (resp: any) => {},
                      (error) => {}
                    );
                  }
                },
                (error) => {
                  this.executiveApiProcessingDone = true;
                }
              );
            } else {
              this.executiveApiProcessingDone = true;
            }
          }

          if (res.done) {
            this.apiProcessingDone = true;
          }
        },
        (error) => {}
      );
    }

  saveExecutiveConfig() {
    this.executiveConfigSaved = true;

    this.summaryData = this.selectedSitesForConfig.map(site => {
      const base = {
        site_id: site.site_id,
        site_name: site.site_name
      };
      if (this.executiveConfigChoice === 'yes' && site.execSelected) {
        return {
          ...base,
          executiveUsername: site.executiveUsername,
          executivePassword: site.executivePassword
        };
      }
      return base;
    });

    this.popupOpen = false;
    this.apiProcessingDone = false;
    setTimeout(() => {
      this.showSummaryPopup = true;
      this.startApiProcessingStatusPolling();
    }, 300);
  }

  saveSelectedSiteIds() {
    const selectedIds = this.availableSites.filter(site => site.selected).map(site => site.site_id);
    localStorage.setItem('selectedSiteIds', JSON.stringify(selectedIds));
  }
}
